import CopyButton from "@components/buttons/CopyButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import { getApiErrorDisplay } from "@services/api/apiError";
import { transactionApiServices } from "@services/apis/Transactions";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

function formatHumanLabel(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  if (!v) return "--";
  return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDob(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return asRecord(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function readPayeeValue(
  payee: Record<string, unknown>,
  keys: string[],
): unknown {
  const entries = Object.entries(payee);
  for (const key of keys) {
    const match = entries.find(
      ([entryKey]) => entryKey.toLowerCase() === key.toLowerCase(),
    );
    if (!match || match[1] == null) continue;
    if (typeof match[1] === "string" && match[1].trim() === "") continue;
    return match[1];
  }
  return undefined;
}

function payeeValueToString(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}

const MERCHANT_PAYEE_FIELDS: {
  keys: string[];
  label: string;
  kind: "text" | "date" | "label";
}[] = [
  { keys: ["entityType", "type"], label: "Type", kind: "label" },
  { keys: ["firstName"], label: "First name", kind: "text" },
  { keys: ["lastName"], label: "Last name", kind: "text" },
  { keys: ["DOB", "dob", "dateOfBirth"], label: "Date of birth", kind: "date" },
  { keys: ["placeOfBirth"], label: "Place of birth", kind: "label" },
];

function MerchantPayeeDetailsBox({
  details,
}: {
  details: Record<string, unknown>;
}) {
  const knownKeys = new Set(
    MERCHANT_PAYEE_FIELDS.flatMap((field) =>
      field.keys.map((key) => key.toLowerCase()),
    ),
  );

  const extraFields = Object.entries(details).filter(
    ([key, value]) =>
      !knownKeys.has(key.toLowerCase()) && payeeValueToString(value) !== "",
  );

  return (
    <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
      <p className="text-text-navbar-active text-[12px] font-semibold underline">
        Counterparty details (Merchant submission):
      </p>
      {MERCHANT_PAYEE_FIELDS.map((field) => {
        const raw = payeeValueToString(readPayeeValue(details, field.keys));
        let value = raw;
        if (raw && field.kind === "date") {
          value = formatDob(raw);
        } else if (raw && field.kind === "label") {
          value = formatHumanLabel(raw);
        }
        return (
          <LabelRow
            key={field.label}
            label={field.label}
            value={value}
            isCopy={field.kind !== "date" && value !== ""}
          />
        );
      })}
      {extraFields.map(([key, fieldValue]) => {
        const value = payeeValueToString(fieldValue);
        return (
          <LabelRow
            key={key}
            label={formatHumanLabel(key.replace(/([a-z])([A-Z])/g, "$1 $2"))}
            value={value}
            isCopy={value !== ""}
          />
        );
      })}
    </div>
  );
}

function formatPayloadJson(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return "";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return trimmed;
    }
  }
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
}

interface CPGTransactionDetailsCounterpartyProps {
  transactionId: number;
  merchantOrderId?: string;
}

const CPGTransactionDetailsCounterparty: React.FC<
  CPGTransactionDetailsCounterpartyProps
> = ({ transactionId, merchantOrderId }) => {
  const [loading, setLoading] = useState(true);
  const [payloadText, setPayloadText] = useState<string | null>(null);
  const [payeeDetails, setPayeeDetails] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchTransactionPayload = async () => {
      setLoading(true);
      setPayloadText(null);
      setPayeeDetails(null);
      setInfoMessage(null);
      setErrorMessage(null);
      try {
        let orderId = merchantOrderId?.trim() ?? "";
        if (!orderId) {
          const details = await transactionApiServices.getCPGTransactionDetails(
            { transactionId },
          );
          if (cancelled) return;
          orderId = details.data?.merchantOrderId?.trim() ?? "";
        }
        if (!orderId) {
          setInfoMessage("No transaction payload found.");
          return;
        }
        const res =
          await transactionApiServices.getTransactionPayloadCounterparty(
            orderId,
          );
        if (cancelled) return;
        const payloadObject = asRecord(res.data?.transactionPayload);
        const payeeEntry = payloadObject
          ? Object.entries(payloadObject).find(
              ([key]) => key.toLowerCase() === "payeedetails",
            )
          : undefined;
        const payee = payeeEntry ? asRecord(payeeEntry[1]) : null;
        if (payee && Object.keys(payee).length > 0) {
          setPayeeDetails(payee);
        }

        const formatted = formatPayloadJson(res.data?.transactionPayload);
        if (formatted) {
          setPayloadText(formatted);
          return;
        }
        setInfoMessage("No transaction payload found.");
      } catch (error: unknown) {
        if (cancelled) return;
        setErrorMessage(getApiErrorDisplay(error));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTransactionPayload();
    return () => {
      cancelled = true;
    };
  }, [merchantOrderId, transactionId]);

  return (
    <div className="flex flex-col gap-[12px] mt-[16px]">
      {payeeDetails ? <MerchantPayeeDetailsBox details={payeeDetails} /> : null}
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <div className="flex flex-row justify-between items-center">
          <p className="text-text-navbar-active text-[12px] font-semibold underline">
            Transaction Payload:
          </p>
          {payloadText ? (
            <CopyButton
              copyText={payloadText}
              copyMessage="Transaction payload copied!"
              buttonSize="24px"
              copyIconSize={12}
            />
          ) : null}
        </div>
        {loading ? (
          <Skeleton height={14} count={6} className="my-1" />
        ) : errorMessage ? (
          <p className="text-[14px] font-medium text-text-status-red">
            {errorMessage}
          </p>
        ) : infoMessage ? (
          <p className="text-[14px] font-medium text-text-subtle">
            {infoMessage}
          </p>
        ) : payloadText ? (
          <pre className="max-h-[360px] bg-bg-page-body border border-border-subtle rounded-lg p-[12px] text-[12px] leading-relaxed text-text-base overflow-auto whitespace-pre-wrap break-all font-mono">
            {payloadText}
          </pre>
        ) : null}
      </div>
    </div>
  );
};

export default CPGTransactionDetailsCounterparty;
